// videoZoom.js — auto-zoom camera playback for imported screen-recording videos.
//
// Ported verbatim (formulas MUST stay identical) from the Chrome recorder
// extension's src/utils/zoom-keyframes.ts (sampleZoom + zoomTransform). The
// recorder bakes a ZoomKeyframe[] camera path onto each video Step
// (step.zoomKeyframes) during recording; the ZIP export carries it into
// guide.json. The screen-training importer (stepToBoxes.js) forwards that array
// into the EnrichedPlayer box initialState, and EnrichedPlayerPlugin drives a
// per-frame CSS transform on the raw <video> from these functions — a
// non-destructive overlay, the clip is never re-encoded. Keep in sync with the
// recorder copy if the camera math ever changes.
//
// A ZoomKeyframe is: { tMs, scale, focusX, focusY, easeMs?, lin? }
//   tMs    — playback time (ms) from the video start
//   scale  — 1 = full frame, >1 zoomed in
//   focusX/Y — 0..1 normalized frame point kept centered
//   easeMs — transition duration INTO this keyframe (from the previous)
//   lin    — linear interp into this keyframe (dense follow-pan) vs easeInOutCubic

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/**
 * Sample the camera at playback time t (ms). Returns { scale, focusX, focusY,
 * scaleAnim }. scaleAnim is true mid-scale-ramp — the renderer must NOT
 * device-px-snap the translate then (see zoomTransform).
 */
export function sampleZoom(kfs, t) {
  if (!kfs || kfs.length === 0) return { scale: 1, focusX: 0.5, focusY: 0.5, scaleAnim: false };
  if (t <= kfs[0].tMs)
    return { scale: kfs[0].scale, focusX: kfs[0].focusX, focusY: kfs[0].focusY, scaleAnim: false };

  let i = 0;
  for (let k = 0; k < kfs.length; k++) {
    if (kfs[k].tMs <= t) i = k;
    else break;
  }
  const a = kfs[i];
  if (i === 0) return { scale: a.scale, focusX: a.focusX, focusY: a.focusY, scaleAnim: false };

  const E = a.easeMs != null ? a.easeMs : 0;
  const local = t - a.tMs;
  if (E <= 0 || local >= E)
    return { scale: a.scale, focusX: a.focusX, focusY: a.focusY, scaleAnim: false };

  const prev = kfs[i - 1];
  const f = a.lin ? local / E : easeInOutCubic(local / E);
  return {
    scale: lerp(prev.scale, a.scale, f),
    focusX: lerp(prev.focusX, a.focusX, f),
    focusY: lerp(prev.focusY, a.focusY, f),
    scaleAnim: prev.scale !== a.scale,
  };
}

/**
 * Convert a sampled camera into a CSS transform for a video element of rendered
 * size W×H. Use with transform-origin: 0 0. Translates so the focus point sits
 * at the container center, clamped so the frame edges never reveal empty space.
 *
 * dpr (devicePixelRatio) snaps the translate to the physical pixel grid to kill
 * the sub-pixel shimmer on constant-scale holds/pans. Pass dpr = 0 to DISABLE
 * the snap — required while the scale itself is animating (scaleAnim), where
 * snapping a fast translate causes vertical judder.
 */
export function zoomTransform(scale, focusX, focusY, W, H, dpr = 1) {
  let tx = W / 2 - focusX * W * scale;
  let ty = H / 2 - focusY * H * scale;
  if (dpr > 0) {
    tx = Math.round(tx * dpr) / dpr;
    ty = Math.round(ty * dpr) / dpr;
  }
  tx = Math.min(0, Math.max(W - W * scale, tx));
  ty = Math.min(0, Math.max(H - H * scale, ty));
  return { tx, ty, scale };
}

// Max wallpaper the card-fly zoom may reveal on a side, as a fraction of the
// card dimension (recorder zoom-keyframes.ts EDGE_REVEAL_FRAC).
export const EDGE_REVEAL_FRAC = 0.15;

/**
 * "Card-fly" zoom (recorder zoom-keyframes.ts cardZoomTransform, verbatim).
 * When the box shows the recorder look, the FRAMED CARD (rounded window +
 * shadow + bar + video) zooms as one unit inside the fixed wallpaper, instead
 * of the raw video zooming inside its window. All inputs are PRE-transform
 * layout px (offsetWidth/offsetLeft — never getBoundingClientRect, which
 * returns the post-transform box and makes the camera feed on its own output):
 *   Wv,Hv  video element size     vx,vy  video top-left within the card
 *   Wc,Hc  card size              Lx,Ly  card top-left within the stage
 *   stageW,stageH  the wallpaper stage the card flies inside
 * transform-origin MUST be "0 0". The pan is clamped so the card reveals at
 * most max(padding, EDGE_REVEAL_FRAC·card) of wallpaper on a side; with no
 * padding (Lx=Ly=0) it collapses to the plain fill clamp.
 */
export function cardZoomTransform(scale, focusX, focusY, g, dpr = 1) {
  const fxc = g.vx + focusX * g.Wv;
  const fyc = g.vy + focusY * g.Hv;
  let tx = g.stageW / 2 - g.Lx - fxc * scale;
  let ty = g.stageH / 2 - g.Ly - fyc * scale;
  if (dpr > 0) {
    tx = Math.round(tx * dpr) / dpr;
    ty = Math.round(ty * dpr) / dpr;
  }
  const revealX = g.Lx > 0 ? Math.max(g.Lx, EDGE_REVEAL_FRAC * g.Wc) : 0;
  const revealY = g.Ly > 0 ? Math.max(g.Ly, EDGE_REVEAL_FRAC * g.Hc) : 0;
  const spanX = g.Wc * scale;
  const spanY = g.Hc * scale;
  tx = Math.min(revealX - g.Lx, Math.max(g.stageW - revealX - g.Lx - spanX, tx));
  ty = Math.min(revealY - g.Ly, Math.max(g.stageH - revealY - g.Ly - spanY, ty));
  return { tx, ty, scale };
}

// Card-look class names: the camera finds the card from the <video> via these.
export const LOOK_CARD_CLASS = "enriched-player-look__card";

function offsetWithin(el, ancestor) {
  let x = 0;
  let y = 0;
  let cur = el;
  while (cur && cur !== ancestor) {
    x += cur.offsetLeft;
    y += cur.offsetTop;
    cur = cur.offsetParent;
  }
  return { x, y };
}

// ── Shared playback pieces (used by both EnrichedPlayerPlugin (visor) and
//    EnrichedPlayerPluginEditor). Kept here so the two renderers stay in sync. ──

/**
 * Auto-zoom camera controller. Drives a CSS transform on a raw <video> from a
 * ZoomKeyframe[] path, synced to the clip's own currentTime. `getEl()` returns
 * the element (may be null until the player mounts — the loop self-heals by
 * polling). Mirrors the recorder PlayerApp rAF loop, incl. the monotonic
 * playback clock that survives a WebM's Infinity-duration currentTime jitter.
 */
export function createZoomCamera(getEl, kfs) {
  let raf = null;
  let clockMs = 0;
  let lastNow = null;
  let lastTarget = null; // element currently carrying the transform (video or card)

  function tick() {
    const v = getEl();
    if (!v) {
      raf = requestAnimationFrame(tick);
      return;
    }
    const now =
      typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
    const ctMs = (v.currentTime || 0) * 1000;
    if (lastNow === null) {
      clockMs = ctMs;
    } else if (v.paused || v.seeking) {
      clockMs = ctMs;
    } else {
      clockMs += (now - lastNow) * (v.playbackRate || 1);
      if (Math.abs(clockMs - ctMs) > 200) clockMs = ctMs; // resync on seek/stall
    }
    lastNow = now;

    const W = v.offsetWidth;
    const H = v.offsetHeight;
    // Hold through a seek: the Infinity-WebM duration probe jumps currentTime
    // to the end and would flash the last (zoomed) keyframe.
    if (W > 0 && H > 0 && !v.seeking) {
      const s = sampleZoom(kfs, clockMs);
      const dpr = s.scaleAnim ? 0 : (typeof window !== "undefined" && window.devicePixelRatio) || 1;
      // Recorder look → card-fly (frame zooms with the video, wallpaper stays).
      const card = v.closest ? v.closest("." + LOOK_CARD_CLASS) : null;
      const stage = card && card.offsetParent;
      const target = card && stage ? card : v;
      if (target !== lastTarget) {
        if (lastTarget) lastTarget.style.transform = "";
        lastTarget = target;
      }
      let t;
      if (target === card) {
        const voff = offsetWithin(v, card);
        t = cardZoomTransform(
          s.scale,
          s.focusX,
          s.focusY,
          {
            Wv: W,
            Hv: H,
            vx: voff.x,
            vy: voff.y,
            Wc: card.offsetWidth,
            Hc: card.offsetHeight,
            Lx: card.offsetLeft,
            Ly: card.offsetTop,
            stageW: stage.offsetWidth,
            stageH: stage.offsetHeight,
          },
          dpr
        );
      } else {
        t = zoomTransform(s.scale, s.focusX, s.focusY, W, H, dpr);
      }
      target.style.transformOrigin = "0 0";
      target.style.transform = `translate(${t.tx}px, ${t.ty}px) scale(${t.scale})`;
      target.style.willChange = "transform";
    }
    raf = requestAnimationFrame(tick);
  }

  return {
    start() {
      if (!Array.isArray(kfs) || kfs.length === 0) return;
      if (raf) return;
      clockMs = 0;
      lastNow = null;
      raf = requestAnimationFrame(tick);
    },
    stop() {
      if (raf) {
        cancelAnimationFrame(raf);
        raf = null;
      }
      lastNow = null;
      if (lastTarget) lastTarget.style.transform = "";
      lastTarget = null;
      const v = getEl();
      if (v) v.style.transform = "";
    },
  };
}

// ── Infinity-duration WebM (MediaRecorder screen recordings) ───────────────
// A MediaRecorder WebM carries no Duration header: <video>.duration stays
// Infinity until the element has been seeked to the end once. react-player
// 1.x then reports NOT Infinity but the end of the `seekable` range — a sliver
// of a second while loading — so its onDuration value must not be trusted for
// these clips. Check the element itself and resolve the real length with a
// throwaway probe element (never seek the playing one).

/** True when the box's own <video> has no usable duration (WebM w/o header). */
export function hasInfiniteDuration(player) {
  const el = player && player.getInternalPlayer && player.getInternalPlayer();
  return !!el && el.duration === Infinity;
}

/** Resolve a WebM's real duration via the seek-to-end trick; null on failure. */
export function probeInfiniteDuration(url) {
  return new Promise((resolve) => {
    if (typeof document === "undefined" || !url) return resolve(null);
    const v = document.createElement("video");
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      const d = v.duration;
      v.removeAttribute("src");
      v.load();
      resolve(Number.isFinite(d) && d > 0 ? d : null);
    };
    const timer = setTimeout(finish, 8000);
    v.preload = "metadata";
    v.muted = true;
    v.addEventListener("loadedmetadata", () => {
      if (Number.isFinite(v.duration)) return finish();
      v.addEventListener("durationchange", () => Number.isFinite(v.duration) && finish());
      v.addEventListener("seeked", finish);
      v.currentTime = 1e101;
    });
    v.addEventListener("error", finish);
    v.src = url;
  });
}

// ── Recorder "look": wallpaper + browser frame around the video ─────────────
//
// Mirrors the recorder's src/utils/video-style.ts (videoFrameStyles +
// browserFrameSpec + frame crop). The importer (stepToBoxes.js) resolves
// guide.metadata.videoStyle into two optional box-state objects:
//   state.videoWallpaper = { background, padding, radius, shadow }
//       background: CSS background string ('' = none); padding/radius: px at the
//       recorder's LOOK_REF_W-wide preview; shadow: bool.
//   state.videoFrame = { cropTop, cropBottom, bar, theme, border, url }
//       cropTop/cropBottom: FRACTIONS (0..0.6) of the video height, already gated
//       on the clip's capture surface; bar: draw the slim macOS strip.
// The recorder sizes these in fixed px inside its ~1000px preview; here every
// length is expressed relative to the frame's own width (%, aspect-ratio, cqw)
// so the look scales with whatever size the box is given. The SAME geometry
// drives the WebM export (videoExport.js) — keep the two in sync.

export const LOOK_REF_W = 1000;
export const FRAME_BAR_H = 34; // recorder minimal-mode bar height (px @ LOOK_REF_W)
export const VIDEO_SHADOW = { y: 20, blur: 60, color: "rgba(0,0,0,0.35)" }; // px @ LOOK_REF_W

export function frameThemeColors(theme) {
  return theme === "light"
    ? {
        bar: "#ececef",
        text: "#5a5a66",
        pill: "#ffffff",
        pillText: "#3a3a44",
        border: "rgba(0,0,0,0.10)",
      }
    : {
        bar: "#2b2b30",
        text: "#a8a8b3",
        pill: "#1b1b20",
        pillText: "#c9c9d4",
        border: "rgba(255,255,255,0.08)",
      };
}

/**
 * Geometry of the composed look, in units of the OUTER frame width (W = 1), or
 * null when the box carries no look (plain video — render exactly as before).
 * `ratio` is the video's natural width/height.
 */
export function videoLookGeometry(state, ratio) {
  const wp = state && state.videoWallpaper;
  const vf = state && state.videoFrame;
  if (!wp && !vf) return null;
  const r = ratio > 0 ? ratio : 16 / 9;
  const ct = vf ? Math.max(0, Math.min(0.6, vf.cropTop || 0)) : 0;
  const cb = vf ? Math.max(0, Math.min(0.6, vf.cropBottom || 0)) : 0;
  const k = Math.max(0.2, 1 - ct - cb); // visible fraction of the video height
  const pad = wp ? Math.max(0, wp.padding || 0) / LOOK_REF_W : 0;
  const radius = wp ? Math.max(0, wp.radius || 0) / LOOK_REF_W : 0;
  const bg = (wp && wp.background) || "";
  // Recorder outerRadius(): concentric with the window when padded.
  const outerRadius = bg && pad > 0 ? radius + Math.round((wp.padding || 0) / 2) / LOOK_REF_W : 0;
  const winW = 1 - 2 * pad;
  const barH = vf && vf.bar ? FRAME_BAR_H / LOOK_REF_W : 0;
  const stageH = (winW * k) / r;
  const winH = barH + stageH;
  const outerH = 2 * pad + winH;
  return {
    aspect: 1 / outerH, // outer width / height
    pad,
    winW,
    winH,
    barH,
    stageH,
    ct,
    k,
    radius,
    outerRadius,
    background: bg,
    shadow: !!(wp && wp.shadow),
    frame: vf || null,
  };
}

// Elliptical % border-radius equal to `len` (in W units) on a w×h box.
function pctRadius(len, w, h) {
  if (!(len > 0)) return undefined;
  return `${(len / w) * 100}% / ${(len / h) * 100}%`;
}

/**
 * Style for the <ReactPlayer> wrapper inside the look's stage: the full-height
 * video is shifted up by the top crop so the stage's overflow clips the baked-in
 * chrome/taskbar strips. Composes with the auto-zoom transform on the <video>.
 */
export function videoLookPlayerStyle(geo) {
  return {
    position: "absolute",
    left: 0,
    width: "100%",
    top: `${-(geo.ct / geo.k) * 100}%`,
    height: `${(1 / geo.k) * 100}%`,
  };
}

// Extra style for the player wrapper when a look is shown: a size container so
// the frame can contain-fit itself (cqw/cqh) inside whatever box size it gets.
export const VIDEO_LOOK_WRAPPER_STYLE = {
  containerType: "size",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
};

function renderLookBar(React, frame) {
  const c = frameThemeColors(frame.theme);
  const u = (px) => `${(px / LOOK_REF_W) * 100}cqw`; // px @ LOOK_REF_W → outer-relative
  const dot = (color) => ({
    width: u(11),
    height: u(11),
    borderRadius: "50%",
    background: color,
    flex: "0 0 auto",
  });
  return React.createElement(
    "div",
    {
      style: {
        height: u(FRAME_BAR_H),
        flex: "0 0 auto",
        background: c.bar,
        display: "flex",
        alignItems: "center",
        gap: u(8),
        padding: `0 ${u(12)}`,
        boxSizing: "border-box",
      },
    },
    React.createElement("div", { style: dot("#ff5f57") }),
    React.createElement("div", { style: dot("#febc2e") }),
    React.createElement("div", { style: dot("#28c840") }),
    React.createElement(
      "div",
      {
        style: {
          flex: "1 1 auto",
          minWidth: 0,
          margin: "0 auto",
          maxWidth: "70%",
          textAlign: "center",
          background: c.pill,
          color: c.pillText,
          fontSize: u(12),
          lineHeight: u(20),
          borderRadius: u(6),
          padding: `0 ${u(10)}`,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      },
      frame.url
    )
  );
}

/**
 * Wrap the player element in the recorder look:
 *   outer (wallpaper + padding, contain-fit, overflow hidden = zoom stage) >
 *   card (radius/shadow/border; the auto-zoom transforms THIS) >
 *   [bar?, stage(overflow hidden, crop) > player]
 * ONE fixed structure regardless of which parts are on, so the <video> never
 * changes parents (a remount would drop the running zoom camera).
 */
export function renderVideoLook(React, geo, player) {
  const frame = geo.frame;
  const c = frame ? frameThemeColors(frame.theme) : null;
  const u = (px) => `${(px / LOOK_REF_W) * 100}cqw`;
  return React.createElement(
    "div",
    {
      className: "enriched-player-look",
      style: {
        position: "relative",
        boxSizing: "border-box",
        width: `min(100cqw, ${geo.aspect * 100}cqh)`,
        aspectRatio: String(geo.aspect),
        background: geo.background || undefined,
        borderRadius: pctRadius(geo.outerRadius, 1, 1 / geo.aspect),
        containerType: "inline-size",
        flex: "0 0 auto",
        // The stage the card flies inside — clips the zoomed card.
        overflow: "hidden",
      },
    },
    React.createElement(
      "div",
      {
        className: LOOK_CARD_CLASS,
        style: {
          // Padding as insets (a % padding would resolve against the PARENT's
          // width, not the frame's): left/right % of the frame width, top/bottom
          // % of its height — pad is in frame-width units, so × aspect.
          position: "absolute",
          left: `${geo.pad * 100}%`,
          right: `${geo.pad * 100}%`,
          top: `${geo.pad * geo.aspect * 100}%`,
          bottom: `${geo.pad * geo.aspect * 100}%`,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderRadius: pctRadius(geo.radius, geo.winW, geo.winH),
          boxShadow: geo.shadow
            ? `0 ${u(VIDEO_SHADOW.y)} ${u(VIDEO_SHADOW.blur)} ${VIDEO_SHADOW.color}`
            : undefined,
          outline: frame && frame.bar && frame.border ? `1px solid ${c.border}` : undefined,
          outlineOffset: "-1px",
        },
      },
      frame && frame.bar ? renderLookBar(React, frame) : null,
      React.createElement(
        "div",
        {
          style: {
            position: "relative",
            flex: "1 1 auto",
            overflow: "hidden",
            background: "#000",
          },
        },
        player
      )
    )
  );
}
